import { ETextInputTypes, TextInput } from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
  ELocationSearchAutocompleteListVariant,
  LocationSearchAutocompleteList,
} from '@/components/common/LocationSearchAutocompleteList/LocationSearchAutocompleteList';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import {
  ILocationSearchAutocompleteListOption,
  useLocationSearchAutocompleteOptions,
} from '@/hooks/useLocationSearchAutocompleteOptions';
import { useLocationSearchSuggestions } from '@/hooks/useLocationSearchSuggestions';
import { TMapboxQuery } from '@/hooks/useMapboxQuery';
import { EDeliveryFields, TDeliveryFields } from '@/services/types/core/delivery.types';

interface IAdressInputProps {
  addressValue: string;
  onChangeAddress?: (inputValue: string) => void;
  onSelectAddress?: (value: ILocationSearchAutocompleteListOption) => void;
  onBlurAddress?: () => void;
  name: EDeliveryFields;
  form: UseFormReturn<TDeliveryFields>;
  placesOption?: any;
  inputClassName?: string;
  label?: string;
  onToggleAutocompleteOptions?: (isOpen: boolean) => void;
  required?: boolean;
  recentSearches?: string[];
  onFocus?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
}

const AddressInput: React.FC<IAdressInputProps> = ({
  addressValue,
  inputClassName,
  onChangeAddress,
  onSelectAddress,
  name,
  onBlurAddress,
  placesOption,
  label,
  onToggleAutocompleteOptions,
  required = true,
  recentSearches = [],
  onFocus,
  form,
}) => {
  const intl = useIntl();
  const [mapboxQuery, setMapboxQuery] = useState<TMapboxQuery | null>(null);
  const { isMobile } = useBreakpoint();
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [shouldAlignOnBottom, setShouldAlignOnBottom] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [shouldRenderDropdown, setShouldRenderDropdown] = useState(false);
  const positionUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const error = form?.formState.errors?.[name];

  const searchSuggestions = useLocationSearchSuggestions(
    mapboxQuery?.text || '',
    mapboxQuery?.location,
    mapboxQuery?.types,
  );

  const suggestions =
    addressValue.length >= 3
      ? searchSuggestions
      : recentSearches.map(val => ({ label: val, sublabel: val, value: val }));

  const { groupedOptions } = useLocationSearchAutocompleteOptions({
    shouldBuildGroupedOptions: true,
    includeCurrentLocation: false,
    suggestions,
  });

  const clearErrors = form?.clearErrors;

  const updateDropdownPosition = useCallback(() => {
    if (!inputRef.current) return;

    const inputRect = inputRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current?.offsetHeight || 0;

    const constrainingElement = document.documentElement;
    const constraintRect =
      constrainingElement === document.documentElement
        ? { top: 0, bottom: window.innerHeight }
        : constrainingElement.getBoundingClientRect();

    const fitsBelow = inputRect.bottom + dropdownHeight <= constraintRect.bottom;
    const fitsAbove = inputRect.top - dropdownHeight >= constraintRect.top;

    const alignBottom = fitsBelow || !fitsAbove;
    setShouldAlignOnBottom(alignBottom);

    const spacing = alignBottom ? 24 : 32; //  mb-6/mt-8

    setDropdownPosition({
      top: alignBottom ? inputRect.bottom + spacing : inputRect.top - dropdownHeight - spacing,
      left: inputRect.left,
      width: inputRect.width,
    });
  }, []);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement, Element>) => {
      setShouldRenderDropdown(true);
      updateDropdownPosition();
      // Small delay to ensure DOM is rendered before animation
      requestAnimationFrame(() => {
        setShowDropdown(true);
      });
      onFocus?.(e);
    },
    [onFocus, updateDropdownPosition],
  );

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      setShowDropdown(false);
      // Wait for animation to complete before unmounting
      setTimeout(() => {
        setShouldRenderDropdown(false);
      }, 150);
    }, 150);
    onBlurAddress?.();
  }, [onBlurAddress]);

  const handleChangeAddress = useCallback(
    async (inputValue: string) => {
      if (!showDropdown) {
        // If the dropdown is not shown, show it
        setShouldRenderDropdown(true);
        requestAnimationFrame(() => {
          setShowDropdown(true);
        });
      }
      if (inputValue === '') {
        clearErrors?.();
      }
      onChangeAddress?.(inputValue);
      setMapboxQuery(inputValue.length < 3 ? null : { text: inputValue, ...placesOption });
    },
    [showDropdown, onChangeAddress, placesOption, clearErrors],
  );

  useEffect(() => {
    if (addressValue) {
      setMapboxQuery({ text: addressValue, ...placesOption });
      clearErrors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (!showDropdown) return;

      if (positionUpdateTimeoutRef.current) {
        clearTimeout(positionUpdateTimeoutRef.current);
      }

      // Throttle to prevent excessive re-renders
      positionUpdateTimeoutRef.current = setTimeout(() => {
        updateDropdownPosition();
      }, 16); // ~60fps throttle
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);

      if (positionUpdateTimeoutRef.current) {
        clearTimeout(positionUpdateTimeoutRef.current);
        positionUpdateTimeoutRef.current = null;
      }
    };
  }, [showDropdown, updateDropdownPosition]);

  useEffect(() => {
    onToggleAutocompleteOptions?.(showDropdown);
  }, [showDropdown, onToggleAutocompleteOptions]);

  // When content changes
  useEffect(() => {
    if (shouldRenderDropdown && groupedOptions?.length) {
      // Ensure DOM is updated
      const animationFrame = requestAnimationFrame(() => {
        updateDropdownPosition();
      });

      return () => {
        cancelAnimationFrame(animationFrame);
      };
    }
  }, [shouldRenderDropdown, groupedOptions?.length, updateDropdownPosition]);

  const formRegisterProps = form?.register?.(name, {
    onChange: (inputValue: string) => {
      handleChangeAddress(inputValue);
      form?.setValue(name, inputValue);
    },
    onBlur: handleBlur,
    required: {
      value: required,
      message: intl.formatMessage({
        defaultMessage: 'Please enter the delivery address.',
        id: 'CcYJyO',
      }),
    },
  });

  const autocompleteList = (
    <LocationSearchAutocompleteList
      groupedOptions={groupedOptions}
      onSelectOption={option => onSelectAddress?.(option)}
      variant={ELocationSearchAutocompleteListVariant.Vertical}
      maxOptionsPerColumn={3}
      maxGroups={1}
      className="md:mx-3"
    />
  );
  return (
    <>
      <TextInput
        type={ETextInputTypes.Text}
        data-testid={name}
        label={
          label ||
          intl.formatMessage({
            defaultMessage: 'Address',
            id: 'e6Ph5+',
          })
        }
        inputValue={addressValue || ''}
        controlled
        className={inputClassName}
        required={required}
        autoComplete="off"
        {...formRegisterProps}
        onChange={(inputValue: string) => {
          handleChangeAddress(inputValue);
          form?.setValue(name, inputValue);
        }}
        ref={inputRef}
        onFocus={handleFocus}
        helpText={error?.message}
        showClearButton
      />
      {shouldRenderDropdown && isMobile && groupedOptions.length > 0 && (
        <div>{autocompleteList}</div>
      )}
      {shouldRenderDropdown &&
        !isMobile &&
        groupedOptions.length > 0 &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
            className={clsx(
              'fixed overflow-hidden bg-white z-[10000] rounded-2xl shadow-100 transition-[transform,opacity] duration-150 ease-out',
              {
                'origin-top': shouldAlignOnBottom,
                'origin-bottom': !shouldAlignOnBottom,
                'opacity-100 scale-100': showDropdown,
                'opacity-0 scale-95': !showDropdown,
              },
            )}>
            <div className="overflow-auto">{autocompleteList}</div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default AddressInput;
